import { Google as GoogleIcon } from '@mui/icons-material';
import { Box, Button, Divider, Stack, TextField, Typography } from '@mui/material';
import { FunctionComponent, SubmitEvent, useState } from 'react';
import { errorMessageForAuthError, sendPasswordReset, signInWithEmail, signInWithGoogle } from '../../Authentication';
import { AccessibleButton } from '../../components/Accessibility';
import { EmailSupportLink } from '../../components/EmailSupportLink';
import { AuthLayout } from '../../components/layout/AuthLayout';
import { Analytics } from '../../data/Analytics';
import { Environment } from '../../util/Environment';

export const LoginScreen: FunctionComponent = () => {
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [error, setError] = useState<string | undefined>(undefined);
	const [success, setSuccess] = useState<string | undefined>(undefined);
	const [loading, setLoading] = useState(false);

	async function handleEmailLogin(e: SubmitEvent<unknown>) {
		e.preventDefault();
		await performAuthFunction(() => signInWithEmail(email, password));
	}

	async function handleGoogleLogin() {
		await performAuthFunction(signInWithGoogle);
	}

	async function handlePasswordReset() {
		if (!email) {
			setError('Please enter your email address first');
		} else {
			await performAuthFunction(() => sendPasswordReset(email));
			setSuccess(`Password reset email sent to ${email}. Please check your inbox, including spam folder.`);
		}
	}

	async function performAuthFunction(signIn: () => Promise<unknown>) {
		setError(undefined);
		setSuccess(undefined);
		setLoading(true);

		try {
			await signIn();
		} catch (err) {
			const message = errorMessageForAuthError(err);
			setError(message);
			Analytics.track('login_failed', { reason: message, error: String(err) });
		} finally {
			setLoading(false);
		}
	}

	return <AuthLayout
		title={Environment.gameName}
		subtitle={`Sign in to your ${Environment.gameName} account`}
		loading={loading} loadingLabel='Signing in'
		error={error}
		success={success}
	>
		<Stack component="form" onSubmit={handleEmailLogin} spacing={2}>
			<TextField
				label="Email" type="email" value={email}
				onChange={(e) => setEmail(e.target.value)}
				fullWidth
				required
				disabled={loading}
				autoComplete="email"
			/>
			<TextField
				label="Password" type="password" value={password}
				onChange={(e) => setPassword(e.target.value)}
				fullWidth
				required
				disabled={loading}
				autoComplete="current-password"
			/>
			<Button
				type="submit"
				variant="contained"
				fullWidth
				disabled={loading}
			>
				Sign In
			</Button>
			<Box sx={{ textAlign: 'right' }}>
				<Button
					variant="text"
					size="small"
					disabled={loading}
					onClick={handlePasswordReset}
				>
					Forgot password?
				</Button>
			</Box>
		</Stack>

		<Divider sx={{ marginBottom: 2 }}>or</Divider>

		<AccessibleButton
			variant="outlined"
			fullWidth
			disabled={loading}
			startIcon={<GoogleIcon />}
			onClick={handleGoogleLogin}
			sx={{ marginBottom: 1 }}
		>
			Sign in with Google
		</AccessibleButton>

		<Divider sx={{ marginY: 2 }} />

		<Typography variant="body2" color="text.secondary" align="center" sx={{ marginBottom: 3 }}>
			To create an account, or for support, contact&nbsp;
			<EmailSupportLink />
		</Typography>
	</AuthLayout>;
};
